// components/Sidebar.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getIcon } from '../utils/icons';
import { toast } from 'react-toastify';
import mobileAppService from '../services/mobileAppService';
const { version } = require('../../package.json');

/**
 * Компонент боковой панели навигации
 * @returns {JSX.Element} - Компонент для рендеринга
 */
const Sidebar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(true);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [supportForm, setSupportForm] = useState({
    subject: 'Цифровая Корзина',
    message: '',
    screenshot: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mobileAppInfo, setMobileAppInfo] = useState(null);

  // Загрузка информации о мобильном приложении
  useEffect(() => {
    const loadMobileAppInfo = async () => {
      try {
        const info = await mobileAppService.getMobileAppInfo();
        setMobileAppInfo(info);
      } catch (error) {
        console.error('Ошибка при загрузке информации о мобильном приложении:', error);
        // Не показываем ошибку пользователю, просто не отображаем кнопку
      }
    };

    if (user) {
      loadMobileAppInfo();
    }
  }, [user]);

  // Обработчик клика на кнопку скачивания приложения
  const handleDownloadApp = () => {
    if (mobileAppInfo?.downloadUrl) {
      window.open(mobileAppInfo.downloadUrl, '_blank');
    } else {
      toast.error('Ссылка для скачивания приложения не найдена');
    }
  };

  // Список элементов меню
  const menuItems = [
    { name: 'Главная', path: '/', icon: 'home', role: 'all' },
    { name: 'Ценники', path: '/price-tags', icon: 'tag', role: 'all' },
    { name: 'Товары', path: '/products', icon: 'box', role: 'all' },
    { name: 'Магазины', path: '/stores', icon: 'store', role: 'all' },
    { name: 'Отчеты', path: '/reports', icon: 'chart', role: 'all' },
    { name: 'Категории', path: '/categories', icon: 'folder', role: 'all' },
    { name: 'Производители', path: '/manufacturers', icon: 'folder', role: 'all' },
    { name: 'Пользователи', path: '/users', icon: 'users', role: 'admin' },
    { name: 'Логи ошибок', path: '/error-logs', icon: 'alert', role: 'admin' },
  ];

  // Переключение видимости боковой панели для мобильных устройств
  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  // Обработчик выхода из системы
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Обработчик открытия модального окна поддержки
  const handleOpenSupport = () => {
    setIsSupportModalOpen(true);
  };

  // Обработчик закрытия модального окна поддержки
  const handleCloseSupport = () => {
    setIsSupportModalOpen(false);
    setSupportForm({
      subject: 'Цифровая Корзина',
      message: '',
      screenshot: null
    });
  };

  // Обработчик изменения полей формы поддержки
  const handleSupportFormChange = (e) => {
    const { name, value } = e.target;
    setSupportForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Обработчик выбора файла скриншота
  const handleScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Проверяем тип файла
      if (!file.type.startsWith('image/')) {
        toast.error('Пожалуйста, выберите изображение');
        return;
      }
      // Проверяем размер файла (максимум 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Размер файла не должен превышать 5MB');
        return;
      }
      setSupportForm(prev => ({
        ...prev,
        screenshot: file
      }));
    }
  };

  // Обработчик отправки формы поддержки
  const handleSupportSubmit = async (e) => {
    e.preventDefault();
    
    if (!supportForm.message.trim()) {
      toast.error('Пожалуйста, введите сообщение');
      return;
    }

    setIsSubmitting(true);

    try {
      // TODO: Реализовать отправку на сервер
      // Пока используем простую отправку через FormData
      const formData = new FormData();
      formData.append('subject', supportForm.subject);
      formData.append('message', supportForm.message);
      if (supportForm.screenshot) {
        formData.append('screenshot', supportForm.screenshot);
      }

      // Здесь будет вызов API для отправки обратной связи
      // const response = await api.post('/support/contact', formData, {
      //   headers: {
      //     'Content-Type': 'multipart/form-data'
      //   }
      // });

      // Временная заглушка - показываем успешное сообщение
      console.log('Форма поддержки:', {
        subject: supportForm.subject,
        message: supportForm.message,
        screenshot: supportForm.screenshot?.name
      });

      toast.success('Ваше сообщение отправлено! Мы свяжемся с вами в ближайшее время.');
      handleCloseSupport();
    } catch (error) {
      console.error('Ошибка при отправке формы поддержки:', error);
      toast.error('Не удалось отправить сообщение. Попробуйте позже.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Удаление прикрепленного скриншота
  const handleRemoveScreenshot = () => {
    setSupportForm(prev => ({
      ...prev,
      screenshot: null
    }));
  };

  // Фильтрация элементов меню на основе роли пользователя
  const filteredMenuItems = menuItems.filter(item => 
    item.role === 'all' || (user && (user.role === item.role || user.role === 'admin'))
  );

  return (
    <>
      {/* Кнопка меню для мобильных устройств */}
      <button 
        className="lg:hidden fixed top-4 left-4 bg-blue-600 text-white p-2 rounded z-50"
        onClick={toggleSidebar}
        aria-label="Переключить меню"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7" />
        </svg>
      </button>

      {/* Затемнение фона при открытом мобильном меню */}
      {isOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={toggleSidebar}
        ></div>
      )}

      {/* Боковая панель */}
      <div className={`lg:flex flex-col w-64 bg-blue-800 text-white min-h-screen fixed lg:sticky top-0 z-50 transition-all duration-300 transform ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="p-4 border-b border-blue-700">
          <h2 className="text-xl font-bold">Цифровая корзина</h2>
          <p className="text-sm text-blue-300">Панель администратора</p>
        </div>
        
        {/* Информация о пользователе */}
        {user && (
          <div className="p-4 border-b border-blue-700">
            <div className="flex items-center mb-2">
              <div className="bg-white rounded-full h-10 w-10 flex items-center justify-center mr-3">
                <span className="text-lg font-medium"><img src='logo192.png'></img></span>
              </div>
              <div>
                <p className="text-sm font-medium">{user.fullName}</p>
                <p className="text-xs text-blue-300">{user.role}</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Навигация */}
        <nav className="flex-1 py-4">
          <ul>
            {filteredMenuItems.map((item, index) => (
              <li key={index}>
                <a 
                  href={item.path}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(item.path);
                    if (!isOpen) setIsOpen(false);
                  }}
                  className={`flex items-center py-2 px-4 text-blue-200 hover:bg-blue-700 transition-colors ${location.pathname === item.path ? 'bg-blue-700 text-white' : ''}`}
                >
                  <span className="mr-3">
                    {getIcon(item.icon)}
                  </span>
                  <span>{item.name}</span>
                </a>
              </li>
            ))}
          </ul>
        </nav>
        <div className='flex flex-col justify-center items-center py-4 px-4'>
          <img src="prod.png" alt="Logo" className="w-32 mb-1" />
          <span className="text-xs text-blue-300 mb-3">Версия {version}</span>
          {mobileAppInfo && (
            <button
              onClick={handleDownloadApp}
              className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center mb-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Скачать Приложение
            </button>
          )}
          <button
            onClick={handleOpenSupport}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Поддержка
          </button>
        </div>
        {/* Кнопка выхода */}
        <div className="p-4 border-t border-blue-700">
          <button 
            onClick={handleLogout}
            className="flex items-center text-blue-200 hover:text-white transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Выйти
          </button>
        </div>
      </div>

      {/* Модальное окно поддержки */}
      {isSupportModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-800">Связь с поддержкой</h2>
                <button
                  onClick={handleCloseSupport}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSupportSubmit}>
                <div className="mb-4">
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                    Тема
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={supportForm.subject}
                    onChange={handleSupportFormChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    readOnly
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Сообщение <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={supportForm.message}
                    onChange={handleSupportFormChange}
                    rows={8}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    placeholder="Опишите вашу проблему или вопрос..."
                    required
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="screenshot" className="block text-sm font-medium text-gray-700 mb-2">
                    Прикрепить скриншот (опционально)
                  </label>
                  <div className="flex items-center gap-4">
                    <label className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors inline-flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Выбрать файл
                      <input
                        type="file"
                        id="screenshot"
                        accept="image/*"
                        onChange={handleScreenshotChange}
                        className="hidden"
                      />
                    </label>
                    {supportForm.screenshot && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <span>{supportForm.screenshot.name}</span>
                        <button
                          type="button"
                          onClick={handleRemoveScreenshot}
                          className="text-red-500 hover:text-red-700"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Максимальный размер файла: 5MB</p>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={handleCloseSupport}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                    disabled={isSubmitting}
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Отправка...
                      </>
                    ) : (
                      'Отправить'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;